import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Save, Plus, Trash2, Link as LinkIcon, FileText, Upload, Loader2, Copy, ExternalLink, ImageIcon, AlignLeft, Eye, EyeOff, Pencil, Code2, Library, Check, ChevronsUpDown } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  useResourceWorkshop, useResourceWorkshopItems, useItemMutations,
  useWorkshopMutations, uploadWorkshopFile, ResourceWorkshopItem,
  useContentBankOptions,
} from "@/hooks/useResourceWorkshops";


const slugify = (s: string) =>
  s.toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 60);

const slugValid = (s: string) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(s) && s.length >= 3 && s.length <= 60;

export default function ResourceWorkshopEditPage() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const { data: workshop } = useResourceWorkshop(isNew ? undefined : id);
  const { create, update } = useWorkshopMutations();
  const [form, setForm] = useState({
    name: "",
    slug: "",
    description: "",
    cover_image_url: "",
    visibility: "public" as "public" | "authenticated",
    is_active: true,
  });
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (workshop) {
      setForm({
        name: workshop.name,
        slug: workshop.slug,
        description: workshop.description || "",
        cover_image_url: workshop.cover_image_url || "",
        visibility: workshop.visibility,
        is_active: workshop.is_active,
      });
      setSlugTouched(true);
    }
  }, [workshop]);

  const handleNameChange = (name: string) => {
    setForm((f) => ({ ...f, name, slug: slugTouched ? f.slug : slugify(name) }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("El nombre es requerido");
    if (!slugValid(form.slug)) return toast.error("Slug inválido", { description: "Solo minúsculas, números y guiones (3-60 chars)" });

    try {
      if (isNew) {
        const created = await create.mutateAsync(form);
        toast.success("Taller creado");
        navigate(`/admin/talleres/${created.id}`);
      } else {
        await update.mutateAsync({ id: id!, ...form });
        toast.success("Cambios guardados");
      }
    } catch (e: any) {
      toast.error("Error al guardar", { description: e.message });
    }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/taller/${form.slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copiado");
  };

  const coverInput = useRef<HTMLInputElement>(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `resource-workshops/covers/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
      const { error } = await supabase.storage.from("content").upload(path, file, { upsert: false });
      if (error) throw error;
      const { data } = supabase.storage.from("content").getPublicUrl(path);
      setForm((f) => ({ ...f, cover_image_url: data.publicUrl }));
      toast.success("Imagen subida");
    } catch (err: any) {
      toast.error("Error al subir imagen", { description: err.message });
    } finally {
      setUploadingCover(false);
      if (coverInput.current) coverInput.current.value = "";
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin/talleres")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-display font-bold">
              {isNew ? "Nuevo Taller" : "Editar Taller"}
            </h1>
            {!isNew && (
              <p className="text-sm text-muted-foreground">/taller/{form.slug}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {!isNew && slugValid(form.slug) && (
            <>
              <Button variant="outline" onClick={copyLink} className="gap-2">
                <Copy className="w-4 h-4" /> Copiar link
              </Button>
              <Button variant="outline" onClick={() => window.open(`/taller/${form.slug}`, "_blank")} className="gap-2">
                <ExternalLink className="w-4 h-4" /> Ver
              </Button>
            </>
          )}
          <Button onClick={handleSave} disabled={create.isPending || update.isPending} className="gap-2">
            <Save className="w-4 h-4" /> Guardar
          </Button>
        </div>
      </div>

      <Tabs defaultValue="info">
        <TabsList>
          <TabsTrigger value="info">Información</TabsTrigger>
          <TabsTrigger value="resources" disabled={isNew}>Recursos</TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle>Datos del taller</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre *</Label>
                  <Input value={form.name} onChange={(e) => handleNameChange(e.target.value)} placeholder="Ej. Taller de Prompting Avanzado" />
                </div>
                <div className="space-y-2">
                  <Label>Slug (URL) *</Label>
                  <Input
                    value={form.slug}
                    onChange={(e) => { setSlugTouched(true); setForm((f) => ({ ...f, slug: e.target.value })); }}
                    placeholder="prompting-avanzado"
                  />
                  <p className="text-xs text-muted-foreground">
                    Disponible en <code>/taller/{form.slug || "tu-slug"}</code>
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Descripción</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={5}
                  placeholder="¿De qué trata este taller? Aparece arriba en la página pública."
                />
              </div>

              <div className="space-y-2">
                <Label>Imagen de portada (opcional)</Label>
                <div className="flex items-center gap-3">
                  {form.cover_image_url ? (
                    <img src={form.cover_image_url} alt="Cover" className="w-32 h-20 object-cover rounded border" />
                  ) : (
                    <div className="w-32 h-20 bg-muted rounded border flex items-center justify-center">
                      <ImageIcon className="w-6 h-6 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex flex-col gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => coverInput.current?.click()} disabled={uploadingCover}>
                      {uploadingCover ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      {form.cover_image_url ? "Cambiar" : "Subir"}
                    </Button>
                    {form.cover_image_url && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => setForm((f) => ({ ...f, cover_image_url: "" }))}>
                        Quitar
                      </Button>
                    )}
                  </div>
                  <input ref={coverInput} type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Visibilidad</Label>
                  <Select value={form.visibility} onValueChange={(v: any) => setForm((f) => ({ ...f, visibility: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">Pública (cualquiera con el link)</SelectItem>
                      <SelectItem value="authenticated">Solo usuarios registrados</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Estado</Label>
                  <div className="flex items-center gap-3 h-10">
                    <Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} />
                    <span className="text-sm">{form.is_active ? "Activo (visible)" : "Inactivo (oculto)"}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="resources" className="mt-4">
          {!isNew && <ResourcesSection workshopId={id!} />}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ResourcesSection({ workshopId }: { workshopId: string }) {
  const { data: items, isLoading } = useResourceWorkshopItems(workshopId);
  const { create, update, remove } = useItemMutations(workshopId);
  const [editing, setEditing] = useState<Partial<ResourceWorkshopItem> | null>(null);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Recursos del taller</CardTitle>
        <Button size="sm" onClick={() => setEditing({ type: "link", title: "", url: "", description: "" })} className="gap-2">
          <Plus className="w-4 h-4" /> Agregar recurso
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {isLoading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : !items?.length ? (
          <p className="text-center text-muted-foreground py-6">Sin recursos. Agrega el primero.</p>
        ) : (
          items.map((it, idx) => (
            <div key={it.id} className={`flex items-center gap-3 p-3 border rounded-lg bg-card ${it.is_hidden ? "opacity-50" : ""}`}>
              <div className="flex-shrink-0">
                {it.type === "link" ? <LinkIcon className="w-5 h-5 text-primary" />
                  : it.type === "text" ? <AlignLeft className="w-5 h-5 text-primary" />
                  : it.type === "embed" ? <Code2 className="w-5 h-5 text-primary" />
                  : it.type === "content" ? <Library className="w-5 h-5 text-primary" />
                  : <FileText className="w-5 h-5 text-primary" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate flex items-center gap-2">
                  {it.title}
                  {it.is_hidden && <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-muted text-muted-foreground">Oculto</span>}
                </p>
                {it.description && <p className="text-xs text-muted-foreground truncate">{it.description}</p>}
                {it.type === "text" ? (
                  <p className="text-xs text-muted-foreground truncate">Contenido enriquecido</p>
                ) : it.type === "content" ? (
                  <p className="text-xs text-muted-foreground truncate">Contenido del banco</p>
                ) : it.type === "embed" ? (
                  <p className="text-xs text-muted-foreground truncate">{it.url ? it.url : "HTML embebido"}</p>
                ) : (
                  <a href={it.url || "#"} target="_blank" rel="noreferrer" className="text-xs text-primary truncate block hover:underline">{it.url}</a>
                )}
              </div>

              <div className="flex items-center gap-1">
                <Button size="icon" variant="ghost" disabled={idx === 0}
                  onClick={() => update.mutate({ id: it.id, sort_order: (items[idx - 1].sort_order) - 1 })}
                  title="Subir">▲</Button>
                <Button size="icon" variant="ghost" disabled={idx === items.length - 1}
                  onClick={() => update.mutate({ id: it.id, sort_order: (items[idx + 1].sort_order) + 1 })}
                  title="Bajar">▼</Button>
                <Button size="icon" variant="ghost"
                  onClick={() => update.mutate({ id: it.id, is_hidden: !it.is_hidden })}
                  title={it.is_hidden ? "Mostrar" : "Ocultar"}>
                  {it.is_hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
                <Button size="icon" variant="ghost" onClick={() => setEditing(it)} title="Editar">
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => {
                  if (confirm(`¿Eliminar "${it.title}"?`)) remove.mutate(it.id);
                }}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            </div>
          ))
        )}
      </CardContent>
      {editing && (
        <ItemEditor
          workshopId={workshopId}
          initial={editing}
          nextOrder={(items?.length ?? 0) * 10}
          onClose={() => setEditing(null)}
          onSubmit={async (payload) => {
            try {
              if (editing.id) await update.mutateAsync({ id: editing.id, ...payload });
              else await create.mutateAsync(payload);
              setEditing(null);
              toast.success("Recurso guardado");
            } catch (e: any) {
              toast.error("Error", { description: e.message });
            }
          }}
        />
      )}
    </Card>
  );
}

function ItemEditor({
  workshopId, initial, nextOrder, onClose, onSubmit,
}: {
  workshopId: string;
  initial: Partial<ResourceWorkshopItem>;
  nextOrder: number;
  onClose: () => void;
  onSubmit: (payload: Partial<ResourceWorkshopItem>) => void;
}) {
  const [type, setType] = useState<"link" | "file" | "text" | "embed" | "content">((initial.type as any) || "link");
  const [title, setTitle] = useState(initial.title || "");
  const [description, setDescription] = useState(initial.description || "");
  const [url, setUrl] = useState(initial.url || "");
  const [content, setContent] = useState(initial.content || "");
  const [contentId, setContentId] = useState<string | null>(initial.content_id || null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const { data: bankOptions, isLoading: loadingBank } = useContentBankOptions();
  const selectedContent = bankOptions?.find((c) => c.id === contentId) || null;
  const [embedMode, setEmbedMode] = useState<"url" | "html">(
    initial.type === "embed" && initial.content && !initial.url ? "html" : "url"
  );
  const [fileMeta, setFileMeta] = useState<{ size?: number; mime?: string }>({ size: initial.file_size || undefined, mime: initial.file_mime || undefined });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const htmlFileRef = useRef<HTMLInputElement>(null);


  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url, size, mime } = await uploadWorkshopFile(workshopId, file);
      setUrl(url);
      setFileMeta({ size, mime });
      if (!title) setTitle(file.name);
    } catch (err: any) {
      toast.error("Error al subir", { description: err.message });
    } finally {
      setUploading(false);
    }
  };

  const handleHtmlFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setContent(text);
      if (!title) setTitle(file.name.replace(/\.html?$/i, ""));
      toast.success("HTML cargado");
    } catch (err: any) {
      toast.error("Error al leer archivo", { description: err.message });
    } finally {
      if (htmlFileRef.current) htmlFileRef.current.value = "";
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-full flex justify-center p-4">
        <Card className="w-full max-w-2xl my-8 flex flex-col max-h-[calc(100vh-4rem)]">
          <CardHeader className="flex-shrink-0">
            <CardTitle>{initial.id ? "Editar recurso" : "Nuevo recurso"}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 overflow-y-auto flex-1">
          <div className="space-y-2">
            <Label>Tipo</Label>
            <Select value={type} onValueChange={(v: any) => setType(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="link">Enlace externo</SelectItem>
                <SelectItem value="file">Archivo adjunto</SelectItem>
                <SelectItem value="text">Texto / Nota</SelectItem>
                <SelectItem value="embed">Embebido (iframe / HTML)</SelectItem>
                <SelectItem value="content">Contenido del banco</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {type === "content" && (
            <div className="space-y-2">
              <Label>Contenido *</Label>
              <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
                    <span className="truncate">
                      {selectedContent ? selectedContent.name : loadingBank ? "Cargando contenidos..." : "Selecciona un contenido del banco"}
                    </span>
                    <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar contenido..." />
                    <CommandList>
                      <CommandEmpty>Sin resultados.</CommandEmpty>
                      <CommandGroup>
                        {(bankOptions || []).map((c) => (
                          <CommandItem
                            key={c.id}
                            value={`${c.name} ${c.content_type ?? ""}`}
                            onSelect={() => {
                              setContentId(c.id);
                              if (!title.trim()) setTitle(c.name);
                              if (!description.trim() && c.description) setDescription(c.description);
                              setPickerOpen(false);
                            }}
                          >
                            <Check className={cn("mr-2 h-4 w-4", contentId === c.id ? "opacity-100" : "opacity-0")} />
                            <span className="truncate">{c.name}</span>
                            <span className="ml-auto text-xs text-muted-foreground shrink-0">
                              {[c.content_type, c.duration].filter(Boolean).join(" · ")}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">
                El recurso queda vinculado: si el contenido cambia en el banco, el taller muestra la versión actual.
              </p>
            </div>
          )}
          <div className="space-y-2">
            <Label>Título *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej. Guía PDF de la sesión" />
          </div>
          <div className="space-y-2">
            <Label>Descripción (opcional)</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>

          {type === "link" && (
            <div className="space-y-2">
              <Label>URL *</Label>
              <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..." />
            </div>
          )}
          {type === "file" && (
            <div className="space-y-2">
              <Label>Archivo *</Label>
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}>
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {url ? "Reemplazar" : "Subir archivo"}
                </Button>
                <input ref={fileRef} type="file" className="hidden" onChange={handleFile} />
              </div>
              {url && <p className="text-xs text-muted-foreground truncate">{url}</p>}
            </div>
          )}
          {type === "text" && (
            <div className="space-y-2">
              <Label>Contenido *</Label>
              <RichTextEditor
                value={content}
                onChange={setContent}
                placeholder="Escribe aquí el texto, notas o instrucciones que verán los asistentes..."
              />
            </div>
          )}
          {type === "embed" && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Origen del embebido</Label>
                <Select value={embedMode} onValueChange={(v: any) => setEmbedMode(v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="url">URL (iframe)</SelectItem>
                    <SelectItem value="html">HTML embebido</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {embedMode === "url" ? (
                <div className="space-y-2">
                  <Label>URL a embeber *</Label>
                  <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.canva.com/design/.../view" />
                  <p className="text-xs text-muted-foreground">El sitio destino debe permitir ser embebido en iframes.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Código HTML *</Label>
                  <Textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    rows={8}
                    className="font-mono text-xs"
                    placeholder='<iframe src="..." width="100%" height="600"></iframe>'
                  />
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => htmlFileRef.current?.click()}>
                      <Upload className="w-4 h-4 mr-2" /> Subir .html
                    </Button>
                    <input ref={htmlFileRef} type="file" accept=".html,.htm,text/html" className="hidden" onChange={handleHtmlFile} />
                    <span className="text-xs text-muted-foreground">Se cargará el contenido del archivo.</span>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button
              onClick={() => {
                if (!title.trim()) return toast.error("Título requerido");
                if (type === "text") {
                  if (!content.trim()) return toast.error("Contenido requerido");
                } else if (type === "content") {
                  if (!contentId) return toast.error("Selecciona un contenido del banco");
                } else if (type === "embed") {
                  if (embedMode === "url" && !url.trim()) return toast.error("URL requerida");
                  if (embedMode === "html" && !content.trim()) return toast.error("HTML requerido");
                } else if (!url.trim()) {
                  return toast.error("URL/archivo requerido");
                }
                onSubmit({
                  type,
                  title,
                  description: description || null,
                  url: type === "text" || type === "content" ? null : type === "embed" ? (embedMode === "url" ? url : null) : url,
                  content: type === "text" ? content : type === "embed" ? (embedMode === "html" ? content : null) : null,
                  content_id: type === "content" ? contentId : null,
                  file_size: type === "content" ? null : fileMeta.size ?? null,
                  file_mime: type === "content" ? null : fileMeta.mime ?? null,
                  sort_order: initial.sort_order ?? nextOrder,
                });

              }}
            >Guardar</Button>
          </div>
        </CardContent>
        </Card>
      </div>
    </div>
  );
}

